import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { restorePlaySubscriptions } from "@/hooks/useSubscription";

const AuthContext = createContext(null);

// One-shot per session: as soon as an authenticated user is known, silently re-sync any
// Google Play subscription purchase that never finished server acknowledgement (releases
// Google's "Confirmez le forfait" hold). Self-guarded: no-op outside the Android app.
let playRestoreAttempted = false;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (!playRestoreAttempted) {
        playRestoreAttempted = true;
        restorePlaySubscriptions();
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Magic link from a guest checkout "suivre ma commande" email (Itération 50) - the
    // session token travels in the URL since the visitor has no password/login to click
    // through with. Promote it to normal Bearer storage once, then scrub it from the URL.
    const params = new URLSearchParams(window.location.search);
    const magicToken = params.get("auth");
    if (magicToken) {
      localStorage.setItem("ams_token", magicToken);
      params.delete("auth");
      const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    // Lie les sessions PostHog au compte (user_id + email) pour que l'admin puisse retrouver
    // les replays d'un client depuis l'onglet Ventes. No-op si PostHog est bloqué.
    if (user?.user_id && typeof window !== "undefined" && window.posthog?.identify) {
      try { window.posthog.identify(user.user_id, { email: user.email }); } catch { /* noop */ }
    }
  }, [user?.user_id, user?.email]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ams_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const guestCheckout = async (email) => {
    const { data } = await api.post("/auth/guest", { email });
    localStorage.setItem("ams_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, language) => {
    const referral_code = localStorage.getItem("ams_referral_code") || undefined;
    const { data } = await api.post("/auth/register", { name, email, password, language, referral_code });
    localStorage.setItem("ams_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const referral_code = localStorage.getItem("ams_referral_code") || undefined;
    const { data } = await api.post("/auth/google/session", { session_id: sessionId, referral_code });
    localStorage.setItem("ams_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem("ams_token");
    setUser(null);
  };

  const updateProfile = async (updates) => {
    const { data } = await api.patch("/auth/me", updates);
    setUser(data);
    return data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
  };

  const deleteAccount = async (password) => {
    await api.delete("/auth/me", { data: { password, confirm: true } });
    localStorage.removeItem("ams_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, login, register, loginWithGoogleSession, guestCheckout, logout, updateProfile, changePassword, deleteAccount, refetch: fetchMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
